/*
 * Buffer Controller
*/

import Event from '../events';
import EventHandler from '../event-handler';
import { logger } from '../utils/logger';
import { ErrorTypes, ErrorDetails } from '../errors';


class BufferController extends EventHandler {

	constructor(hls) {
		super(hls,
			Event.MEDIA_ATTACHING,
			Event.MEDIA_DETACHING,
			Event.MANIFEST_PARSED,
			Event.BUFFER_RESET,
			Event.BUFFER_APPENDING,
			Event.BUFFER_CODECS,
			Event.BUFFER_EOS,
			Event.BUFFER_FLUSHING,
		//Mactrix additions start
			Event.BUFFER_SOURCE_SEEKING,
			Event.AUDIO_TRACK_UPDATED,
		//Mactrix additions end
			Event.LEVEL_PTS_UPDATED,
			Event.LEVEL_UPDATED
		);

	// the value that we have set mediasource.duration to
	// (the actual duration may be tweaked slighly by the browser)
		this._msDuration = null;
	// the value that we want to set mediaSource.duration to
		this._levelDuration = null;
		this._playlistDuration = null;
		this._audioTrackDuration = null;
		this._audioPlaylistDuration = null;
		this._needsDetach = false;
		this._firstAudioReceived = false;
		this._firstVideoReceived = false;
		this._firstAudioDts = -1;
		this._firstVideoDts = -1;
		this._firstAudioStart = -1;
		this._firstVideoStart = -1;
		this._syncChecked = false;
		this._sbEndedFound = false;
		this._readyForData = false; //Used to limit operations while a flush is pending. Can be extended to cover more if required

		//Old flushing behaviour. Disabled by default.
		this._clampFlush = false; //this.hls.browFirefox;
		this._rangeByRangeFlush = this._clampFlush;


	// Source Buffer listeners
		this.onsbue = this.onSBUpdateEnd.bind(this);
		this.onsbe = this.onSBUpdateError.bind(this);
		this.onsba = this.onSBAbort.bind(this);

		this.pendingTracks = {};
		this.tracks = {};
	}

	destroy() {
		EventHandler.prototype.destroy.call(this);
	//memory clean up
		this._levelDuration = null;
		this._playlistDuration = null;
		this._audioTrackDuration = null;
		this._audioPlaylistDuration = null;
		this._msDuration = null;
		this.flushRange = null;
		this.onsbe = null;
		this.onsbue = null;
		this.onsba = null;
		this.parent = null;
		this.pendingTracks = null;
	//this.segments = null;
	//this.sourceBuffer = null;
		this.tracks = null;
	//this.hls = null;
	}

	onLevelPtsUpdated(data) {
		let type = data.type;
		let audioTrack = this.tracks.audio;

	// Adjusting `SourceBuffer.timestampOffset` (desired point in the timeline where the next frames should be appended)
	// in Chrome browser when we detect MPEG audio container and time delta between level PTS and `SourceBuffer.timestampOffset`
	// is greater than 100ms (this is enough to handle seek for VOD or level change for LIVE videos). At the time of change we issue
	// `SourceBuffer.abort()` and adjusting `SourceBuffer.timestampOffset` if `SourceBuffer.updating` is false or awaiting `updateend`
	// event if SB is in updating state.
	// More info here: https://github.com/video-dev/hls.js/issues/332#issuecomment-257986486

		if (type === 'audio' && audioTrack && audioTrack.container === 'audio/mpeg') { // Chrome audio mp3 track
			let audioBuffer = this.sourceBuffer.audio;
			let delta = Math.abs(audioBuffer.timestampOffset - data.start);

			// adjust timestamp offset if time delta is greater than 100ms
			if (delta > 0.1) {
				let updating = audioBuffer.updating;

				try {
					logger.warn('audioBuffer.abort()');
					audioBuffer.abort();
				}
				catch (err) {
					updating = true;
					logger.warn('can not abort audio buffer: ' + err);
				}

				if (!updating) {
					logger.warn('change mpeg audio timestamp offset from ' + audioBuffer.timestampOffset + ' to ' + data.start);
					audioBuffer.timestampOffset = data.start;
				}
				else {
					this.audioTimestampOffset = data.start;
				}
			}
		}
	}

	onManifestParsed(data) {
		let audioExpected = data.audio;
		let videoExpected = data.video;
		let sourceBufferNb = 0;

	// in case of alt audio 2 BUFFER_CODECS events will be triggered, one per stream controller
	// sourcebuffers will be created all at once when the expected nb of tracks will be reached
	// in case alt audio is not used, only one BUFFER_CODEC event will be fired from main stream controller
	// it will contain the expected nb of source buffers, no need to compute it
		if (data.altAudio && (audioExpected || videoExpected)) {
			sourceBufferNb = (audioExpected ? 1 : 0) + (videoExpected ? 1 : 0);
			logger.log(`${sourceBufferNb} sourceBuffer(s) expected`);
		}
		this.sourceBufferNb = sourceBufferNb;
	}

	onMediaAttaching(data) {
		let media = this.media = data.media;
		if (media) {
			// setup the media source
			var ms = this.mediaSource = new MediaSource();

			//Media Source listeners
			this.onmso = this.onMediaSourceOpen.bind(this);
			this.onmse = this.onMediaSourceEnded.bind(this);
			this.onmsc = this.onMediaSourceClose.bind(this);
			ms.addEventListener('sourceopen', this.onmso);
			ms.addEventListener('sourceended', this.onmse);
			ms.addEventListener('sourceclose', this.onmsc);

			// link video and media Source
			media.src = URL.createObjectURL(ms);

			this.readyForData = true;
		}
	}

	onMediaDetaching() {
		logger.log('media source detaching');
		var ms = this.mediaSource;
		if (ms) {
			let notReady = this.checkEos();

			if (notReady) {
				this._needsDetach = true;
				this._needsEos = true;
			}
			else {
				this.doDetach();
			}
		}
		else {
			this.hls.trigger(Event.MEDIA_DETACHED);
		}
	}

	onMediaSourceOpen() {
		logger.log('media source opened');
		this.hls.trigger(Event.MEDIA_ATTACHED, { media : this.media });
		let mediaSource = this.mediaSource;
		if (mediaSource) {

			// once received, don't listen anymore to sourceopen event
			mediaSource.removeEventListener('sourceopen', this.onmso);
		}
		this.checkPendingTracks();
	}

	checkPendingTracks() {
	// if any buffer codecs pending, check if we have enough to create sourceBuffers
		let pendingTracks = this.pendingTracks;
		let pendingTracksNb = Object.keys(pendingTracks).length;

	// if any pending tracks and (if nb of pending tracks gt or equal than expected nb or if unknown expected nb)
		if (pendingTracksNb && ( this.sourceBufferNb <= pendingTracksNb || this.sourceBufferNb === 0) ) {
			// ok, let's create them now !
			this.createSourceBuffers(pendingTracks);
			this.pendingTracks = {};

			// append any pending segments now !
			this.doAppending();
		}
	}

	onMediaSourceClose() {
		logger.log('media source closed');
	}

	onMediaSourceEnded() {
		logger.log('media source ended');
	}

	onSBUpdateEnd() {
	// update timestampOffset
		if (this.audioTimestampOffset) {
			let audioBuffer = this.sourceBuffer.audio;
			logger.warn('change mpeg audio timestamp offset from ' + audioBuffer.timestampOffset + ' to ' + this.audioTimestampOffset);
			audioBuffer.timestampOffset = this.audioTimestampOffset;
			delete this.audioTimestampOffset;
		}

		if (this._needsFlush) {
			this.doFlush();
		}

		if (this._needsEos) {
			this.checkEos();
		}
		// Mactrix addtions start
		if (this._needsDetach) {
			this.doDetach();
		}
		// Mactrix addtions end

		this.appending = false;
		let parent = this.parent;

		// count nb of pending segments waiting for appending on this sourcebuffer
		let pending = this.segments.reduce( (counter, segment) => (segment.parent === parent) ? counter + 1 : counter, 0);
		this.hls.trigger(Event.BUFFER_APPENDED, { parent : parent, pending : pending });

		// don't append in flushing mode
		if (!this._needsFlush) {
			this.doAppending();
		}

		this.updateMediaElementDuration();
	}

	onSBUpdateError(event) {
		logger.error('sourceBuffer error:', event);
	// according to http://www.w3.org/TR/media-source/#sourcebuffer-append-error
	// this error might not always be fatal (it is fatal if decode error is set, in that case
	// it will be followed by a mediaElement error ...)
		this.hls.trigger(Event.ERROR, { type: ErrorTypes.MEDIA_ERROR, details: ErrorDetails.BUFFER_APPENDING_ERROR, fatal: false });
	// we don't need to do more than that, as according to the spec, updateend will be fired just after
	}


	onSBAbort() {
		logger.log('sourceBuffer abort');
	}


	onBufferReset() {
		this.doRemoveSourceBuffer();
		this.flushRange = [];
		this.segments = [];
		this._msDuration = null;

		this._levelDuration = null;
		this._playlistDuration = null;
		this._audioTrackDuration = null;
		this._audioPlaylistDuration = null;

		this._sbEndedFound = false;
		this._resetSyncCheck(true);
	}

//Mactrix additions start
	onBufferSourceSeeking() {
//	this._resetSyncCheck(false); //Disable STREAM_SYNC_FAILED failed on seek
	}


	_resetSyncCheck(force) {
		if ((this._firstAudioReceived && this._firstVideoReceived) || force === true) {
			this._syncChecked = false;
			this._firstAudioReceived = false;
			this._firstVideoReceived = false;
			this._firstAudioDts = -1;
			this._firstVideoDts = -1;
			this._firstAudioStart = -1;
			this._firstVideoStart = -1;
		}
	}

//Mactrix additions end

	onBufferCodecs(tracks) {
	// if source buffer(s) not created yet, appended buffer tracks in this.pendingTracks
	// if sourcebuffers already created, do nothing ...
		if (Object.keys(this.sourceBuffer).length === 0) {
			for (var trackName in tracks) { //eslint-disable-line guard-for-in
				this.pendingTracks[trackName] = tracks[trackName];
			}

			let mediaSource = this.mediaSource;
			if (mediaSource && mediaSource.readyState === 'open') {
				// try to create sourcebuffers if mediasource opened
				this.checkPendingTracks();
			}
		}
	}


	createSourceBuffers(tracks) {
		let sourceBuffer = this.sourceBuffer;
		let	mediaSource = this.mediaSource;

		for (let trackName in tracks) {
			if(!sourceBuffer[trackName]) {
				let track = tracks[trackName];
				// use levelCodec as first priority
				let codec = track.levelCodec || track.codec;
				let mimeType = `${track.container};codecs=${codec}`;

				//Mactrix addition start
				for (let i = 0; i < this.hls.levels.length; i++) {
					if(track.container.indexOf('video') > -1 && this.hls.levels[i].videoCodec === undefined) {
						this.hls.levels[i].videoCodec = codec;
					}
					else if(track.container.indexOf('audio') > -1 && this.hls.levels[i].audioCodec === undefined) {
						this.hls.levels[i].audioCodec = codec;
					}
				}
				//Mactrix addition end

				logger.log(`creating sourceBuffer(${mimeType})`);
				try {
					let sb = sourceBuffer[trackName] = mediaSource.addSourceBuffer(mimeType);


//					this.mediaSource.removeSourceBuffer(sb);
//					sb = sourceBuffer[trackName] = mediaSource.addSourceBuffer(mimeType);


					sb.addEventListener('updateend', this.onsbue);
					sb.addEventListener('error', this.onsbe);
					sb.addEventListener('abort', this.onsba);
					this.tracks[trackName] = { codec: codec, container: track.container };
					sb.frags = []; //Mactrix addition
					track.buffer = sb;
				}
				catch(err) {
					logger.error(`error while trying to add sourceBuffer:${err.message}`);
					this.hls.trigger(Event.ERROR, {
						type: ErrorTypes.MEDIA_ERROR,
						details: ErrorDetails.BUFFER_ADD_CODEC_ERROR,
						fatal: false,
						err: err,
						mimeType : mimeType,
					});
				}
			}
		}
		this.hls.trigger(Event.BUFFER_CREATED, { tracks : tracks } );
	}

	onBufferAppending(data) {
		// Detect potential sync issues by detecting larger than expected differences between audio and video segments
		if (!this._syncChecked) {
			if (data.type === 'audio' && data.dts !== undefined && !this._firstAudioReceived) {
				this._firstAudioDts = data.dts;
				this._firstAudioStart = data.start;
				this._firstAudioReceived = true;
			//logger.log(`SEEKSYNC firstAudioDts :${this._firstAudioDts} firstAudioStartPTS :${this._firstAudioStart}`);
			}
			else if (data.type === 'video' && data.dts !== undefined && !this._firstVideoReceived) {
				this._firstVideoDts = data.dts;
				this._firstVideoStart = data.start;
				this._firstVideoReceived = true;
			//logger.log(`SEEKSYNC firstVideoDts :${this._firstVideoDts} firstVideoStartPTS :${this._firstVideoStart}`);
			}
			if (this._firstAudioReceived && this._firstVideoReceived ) {
				let ptsDiff = (this._firstAudioStart - this._firstVideoStart) * 90000;
				let dtsDiff = Math.abs( (this._firstVideoDts + ptsDiff) - this._firstAudioDts);
				let maxDiff = (data.targetDuration * 67500); // 75% of 90000

				//logger.log(`Detected Diff :${dtsDiff} : Max :${maxDiff}`);

				if (dtsDiff > maxDiff ) {
					logger.warn('onBufferAppending STREAM_SYNC_FAILED');
					this.hls.trigger(Event.ERROR, { type: ErrorTypes.MUX_ERROR, details: ErrorDetails.STREAM_SYNC_FAILED, fatal: false });
				}
				this._syncChecked = true;
			}
		}

		if (!this.segments) {
			this.segments = [data];
		}
		else {
			this.segments.push(data);
		}
		if (!this._needsFlush) {
			this.doAppending();
		}
		else {
			logger.log('onBufferAppending: flush needed. Flushing....');
			this.doFlush();
		}
	}

	onBufferAppendFail(data) {
		logger.error('sourceBuffer error:', data.event);
	// according to http://www.w3.org/TR/media-source/#sourcebuffer-append-error
	// this error might not always be fatal (it is fatal if decode error is set, in that case
	// it will be followed by a mediaElement error ...)
		this.hls.trigger(Event.ERROR, { type: ErrorTypes.MEDIA_ERROR, details: ErrorDetails.BUFFER_APPENDING_ERROR, fatal: false });
	}

  // on BUFFER_EOS mark matching sourcebuffer(s) as ended and trigger checkEos()
	onBufferEos(data) {
		var sb = this.sourceBuffer;
		let dataType = data.type;
		for(let type in sb) {
			if (!dataType || type === dataType) {
				if (!sb[type].ended) {
					sb[type].ended = true;
					logger.log(`${type} sourceBuffer now EOS`);
				}
			}
		}
		this.checkEos();
	}

 // if all source buffers are marked as ended, signal endOfStream() to MediaSource.
	checkEos() {
		let sb = this.sourceBuffer;
		let mediaSource = this.mediaSource;
		let sbNotEndedFound = false;

		if (!mediaSource || mediaSource.readyState !== 'open') {
			this._needsEos = false;
			return;
		}

		for(let type in sb) {
			if( type === 'audio' || type === 'video') {
				let sbobj = sb[type];

	/* Mactrix removed
		if (!sbobj.ended) {
			return;
		}
	*/
				if(sbobj.updating) {
					this._needsEos = true;
					return;
				}

				if (sbobj.ended) {
					this._sbEndedFound = true;
				}
				else if (this._sbEndedFound && this._needsEos) {
					sbobj.ended = true;
					logger.log(`${type} sourceBuffer now set to Ended`);
				}
				else {
					sbNotEndedFound = true;
				}
			}
		}
		if (!this._sbEndedFound) {
			return;
		}
		else if (this._sbEndedFound && sbNotEndedFound) {
			this._needsEos = true;
			return;
		}

		logger.log('all media data available, signal endOfStream() to MediaSource and stop loading fragment');
		//Notify the media element that it now has all of the media data
		try {
			mediaSource.endOfStream();
		}
		catch(e) {
			logger.warn('exception while calling mediaSource.endOfStream()');
		}
		this._needsEos = false;
	}


	onBufferFlushing(data) {
		this.readyForData = false;
		this.flushRange.push({ start: data.startOffset, end: data.endOffset, type : data.type });
		// attempt flush immediately
		this.doFlush();
	}

	onLevelUpdated(event) {
		let details = event.details;
		if (details.fragments.length === 0) {
			return;
		}

		logger.log(`onLevelUpdated details.totalduration:${details.totalduration} details.fragments[0].start:${details.fragments[0].start} event.sliding:${event.sliding}`);

		//forbid seeking to start of sliding window by shifting start of window in by one target duration
		if(event.sliding) {
			this._playlistDuration = details.totalduration - details.targetduration;
		}
		else {
			this._playlistDuration = details.totalduration;
		}

		this._levelDuration = details.totalduration + details.fragments[0].start;
		this.updateMediaElementDuration();

		//this.hls.trigger(Event.LEVEL_DURATION_UPDATED, { playlistDuration: playlistDuration, totalDuration: this._levelDuration }); //Mactrix addition
		this.updateLevelDuration(); //Mactrix line added
	}

  //Mactrix additions start
  //factor audio in sliding window calculation
  //TODO: Move this, as well as onLevelUpdated/updateMediaElementDuration, to the stream coordinator
	onAudioTrackUpdated(event) {
		let details = event.details;
		if (details.fragments.length === 0) {
			return;
		}

		//logger.log(`onAudioTrackUpdated details.totalduration:${details.totalduration} details.fragments[0].start:${details.fragments[0].start} event.sliding:${event.sliding}`);

		if(event.sliding) {
			this._audioPlaylistDuration = details.totalduration - details.targetduration;
		}
		else {
			this._audioPlaylistDuration = details.totalduration;
		}

		this._audioTrackDuration = details.totalduration + details.fragments[0].start;
		this.updateMediaElementDuration();
		this.updateLevelDuration();
	}

	updateLevelDuration() {
		if(this.hls.streamCoordinator._audioControllersActive && this._audioTrackDuration && this._audioPlaylistDuration) {
			let start;
			let end;
			let duration;

			if (this._levelDuration && this._playlistDuration) {
				end = Math.min(this._levelDuration, this._audioTrackDuration);
				start = Math.max(this._levelDuration - this._playlistDuration, this._audioTrackDuration - this._audioPlaylistDuration);
			}
			else {
				end = this._audioTrackDuration;
				start = this._audioTrackDuration - this._audioPlaylistDuration;
			}
			duration = end - start;

			//logger.log(`updateLevelDuration duration:${duration} totalDuration:${end} start:${start} this._levelDuration:${this._levelDuration} this._audioTrackDuration:${this._audioTrackDuration} this._playlistDuration:${this._playlistDuration} this._audioPlaylistDuration:${this._audioPlaylistDuration}`);

			this.hls.trigger(Event.LEVEL_DURATION_UPDATED, { playlistDuration: duration, totalDuration: end });
		}
		else {
			//logger.log(`updateLevelDuration this._levelDuration:${this._levelDuration} this._audioTrackDuration:${this._audioTrackDuration} this._playlistDuration:${this._playlistDuration} this._audioPlaylistDuration:${this._audioPlaylistDuration}`);

			this.hls.trigger(Event.LEVEL_DURATION_UPDATED, { playlistDuration: this._playlistDuration, totalDuration: this._levelDuration });
		}
	}
  //Mactrix additions end

  // https://github.com/video-dev/hls.js/issues/355
	updateMediaElementDuration() {
		let media = this.media;
		let mediaSource = this.mediaSource;
		let sourceBuffer = this.sourceBuffer;
		let levelDuration = this._levelDuration;

		if (levelDuration === null || !media || !mediaSource || !sourceBuffer || media.readyState === 0 || mediaSource.readyState !== 'open') {
			return;
		}

		for (let type in sourceBuffer) {
			if (sourceBuffer[type].updating) {
				// can't set duration whilst a buffer is updating
				return;
			}
		}

		if (this._msDuration === null) {
			// initialise to the value that the media source is reporting
			this._msDuration = mediaSource.duration;
		}

		let duration = media.duration;

		// levelDuration was the last value we set.
		// not using mediaSource.duration as the browser may tweak this value
		// only update mediasource duration if its value increase, this is to avoid
		// flushing already buffered portion when switching between quality level
		if ((levelDuration > this._msDuration && levelDuration > duration) || (duration === Infinity || isNaN(duration) )) {
			logger.log(`Updating mediasource duration to ${levelDuration.toFixed(3)}`);
			this._msDuration = mediaSource.duration = levelDuration;
		}
	}


	doFlush() {
		let result = undefined;
		let flushComplete = false;

		if(this.flushRange) {
			logger.log(`doFlush: ${this.flushRange.length} in queue`);

			// A successful valid remove will block the sourcebuffer so anything other than nothingToFlush
			// will exit this loop - SIEG
			while(this.flushRange.length &&
				(result === undefined || (result.wasUpdating === false && result.didError === false && result.didFlush === false ))) {
				var range = this.flushRange[0];

				result = this.flushBuffer(range.start, range.end, range.type);
				if ( result.didFlush || result.nothingToFlush || result.didError ) {
					this.flushRange.shift();
				}
			}

			logger.log(`doFlush: Post flush ${this.flushRange.length} in queue`);

			if (this.flushRange.length === 0 && (result === undefined || ( result && ( result.didFlush === false && result.nothingToFlush === true )))) {
				// everything flushed and updated
				flushComplete = true;
				logger.log('doFlush: Flush completed');
			}
			else if (result && (result.wasUpdating || result.didFlush )) {
				//Flush hasn't finished yet really. Wait for SB update end to retrigger a flush
				this._needsFlush = true;
				logger.log('doFlush: waiting for last flush to complete');
			}
			else if (result && result.didError) {
				flushComplete = true;
				logger.warn('doFlush: Error while flushing');
			}
			else {
				flushComplete = true;
				logger.warn('doFlush: Failed to flush');
			}
		}
		else {
			flushComplete = true;
			logger.warn('doFlush: queue invalid');
		}

		if (flushComplete === true) {
			this._needsFlush = false;
			this.readyForData = true;
			//Clear flush buffer? - SIEG
			this.hls.trigger(Event.BUFFER_FLUSHED);
		}
	}

	doAppending() {
		let hls = this.hls;
		let sourceBuffer = this.sourceBuffer;
		let segments = this.segments;

		if (Object.keys(sourceBuffer).length) {
			if (this.media.error) {
				this.segments = [];
				logger.log('trying to append although a media error occurred, flush segment and abort');
				return;
			}
			if (this.appending) {
				//logger.log(`sb appending in progress`);
				return;
			}
			if (segments && segments.length) {
				let segment = segments.shift();
				try {
					let type = segment.type;
					let sb = sourceBuffer[type];

					if(sb) {
						if(!sb.updating) {
							// reset sourceBuffer ended flag before appending segment
							sb.ended = false;
							//logger.log(`appending ${segment.content} ${type} SB, size:${segment.data.length}, ${segment.parent}`);

							if(segment.sn !== undefined) {
								sourceBuffer[type].frags[segment.sn] = {
									level:segment.level,
									maxLevel:hls.autoLevelCapping,
									start:segment.start,
									end:segment.end,
								};
							}

							this.parent = segment.parent;
							sb.appendBuffer(segment.data);
							this.appendError = 0;
							this.appended++;
							this.appending = true;
						}
						else {
							segments.unshift(segment);
						}
					}
					else {
						// in case we don't have any source buffer matching with this segment type,
						// it means that Mediasource fails to create sourcebuffer
						// discard this segment, and trigger update end
						this.onSBUpdateEnd();
					}
				}
				catch(err) {
					// in case any error occurred while appending, put back segment in segments table
					logger.warn(`error while trying to append buffer:${err.message}`);
					segments.unshift(segment);
					var event = { type: ErrorTypes.MEDIA_ERROR, parent : segment.parent };
					if(err.code !== 22) {
						if (this.appendError) {
							this.appendError++;
						}
						else {
							this.appendError = 1;
						}
						event.details = ErrorDetails.BUFFER_APPEND_ERROR;

						/*
							with UHD content, we could get loop of quota exceeded error until
							browser is able to evict some data from sourcebuffer. retrying help recovering this
						*/

						if (this.appendError > hls.config.appendErrorMaxRetry) {
							logger.log(`fail ${hls.config.appendErrorMaxRetry} times to append segment in sourceBuffer`);
							segments = [];
							event.fatal = true;
							hls.trigger(Event.ERROR, event);
							return;
						}
						else {
							event.fatal = false;
							hls.trigger(Event.ERROR, event);
						}
					}
					else {
						// QuotaExceededError: http://www.w3.org/TR/html5/infrastructure.html#quotaexceedederror
						// let's stop appending any segments, and report BUFFER_FULL_ERROR error
						this.segments = [];
						event.details = ErrorDetails.BUFFER_FULL_ERROR;
						event.fatal = false;
						hls.trigger(Event.ERROR, event);
						return;
					}
				}
			}
		}
	}

  /*
	flush specified buffered range,
	return true once range has been flushed.
	as sourceBuffer.remove() is asynchronous, flushBuffer will be retriggered on sourceBuffer update end
  */
	flushBuffer(startOffset, endOffset, typeIn) {
		let sourceBuffer = this.sourceBuffer;
		let didFlush = false;
		let wasUpdating = false;
		let didError = false;
		let nothingToFlush = false;

		if (Object.keys(sourceBuffer).length) {
			logger.log(`flushBuffer: pos:${this.media.currentTime.toFixed(3)} start:${startOffset} end:${endOffset} type:${typeIn}`);
			let sb;

			for (var type in sourceBuffer) {
				// check if sourcebuffer type is defined (typeIn): if yes, let's only flush this one
				// if no, let's flush all sourcebuffers
				if (typeIn && type !== typeIn) {
					continue;
				}
				sb = sourceBuffer[type];
				// we are going to flush buffer, mark source buffer as 'not ended'
				sb.ended = false;
				if (!sb.updating) {
					try {
						if (sb.buffered.length === 0) {
							nothingToFlush = true;
							logger.log('flushBuffer: Buffer empty');
						}
						else {
							let flushStart = startOffset;
							let flushEnd = endOffset;

							if ( this._rangeByRangeFlush ) {
								let rangeFound = this._nextRange(sb, startOffset, endOffset);

								if ( rangeFound.success ) {
									if (this._clampFlush === true && endOffset === Number.POSITIVE_INFINITY) {
										flushStart = rangeFound.start;
										flushEnd = rangeFound.end;
									}
									else {
										flushStart = Math.max(rangeFound.start, startOffset);
										flushEnd = Math.min(rangeFound.end, endOffset);
									}
								}
							}

							if (flushStart === Number.NEGATIVE_INFINITY || flushStart === 0) {
								flushStart = Math.max(sb.buffered.start(0), 0);
							}
							if (flushEnd === Number.POSITIVE_INFINITY) {
								flushEnd = sb.buffered.end(sb.buffered.length - 1);
							}

							if (flushEnd - flushStart > 0.01 ) {
								didFlush = true;
								logger.log(`flushBuffer: requested flush for ${type} [${flushStart},${flushEnd}], pos:${this.media.currentTime}`);
								sb.remove(flushStart, flushEnd);
							}
							else {
								nothingToFlush = true;
								logger.log('flushBuffer: nothing to flush in this range');
							}
						}
					}
					catch(e) {
						didError = true;
						logger.warn('flushBuffer: error while accessing sourcebuffer, it might have been removed from MediaSource');
					}
				}
				else {
					wasUpdating = true;
					logger.log('flushBuffer: cannot flush, ' + type + ' sb updating in progress');
				}
			}
		}
		else {
			//Is this even possible?
			nothingToFlush = true;
			logger.log('flushBuffer: Sourcebuffer has no buffers');
		}

		if ( !didFlush && !didError && !nothingToFlush && !wasUpdating ) {
			didError = true;
			logger.warn('flushBuffer: Unexpected state while flushing');
		}

		let result = { didFlush, didError, wasUpdating, nothingToFlush };

		return result;
	}

  //critera for smart flush:
  //keep currently playing frag and next frag
  //keep anything at or above the current level
  //flush anything 2 or more below current level
  //if 1 below current level, check the max at that time, if at max, flush
	flushLevels(startOffset, endOffset, currentFrag) {
		logger.log('flushLevels');

		let currentLevel = this.hls.nextLoadLevel;
		let nextRange = { start: undefined, end: undefined };
		this.readyForData = false;
		this.hls.trigger(Event.BUFFER_WAIT);

		for(let type in this.sourceBuffer) {
			if( type === 'audio' || type === 'video') {
				this.sourceBuffer[type].frags.forEach((frag, sn, frags) => {
					//we want to leave the current frag and the next alone
					if(Number(sn) === currentFrag || Number(sn) === currentFrag + 1 || Number(sn) === currentFrag - 1) {
						if (nextRange.start !== undefined) {
							this.flushRange.push({ start: nextRange.start, end: nextRange.end, type: type });
							nextRange.start = undefined;
							nextRange.end = undefined;
						}
						return;
					}

					if(frag === undefined) {
					//we've already flushed this one, ignore
						return;
					}

					let flushingFrag = false;

					if(frag.end > startOffset && frag.start < endOffset) {
						if(frag.level < currentLevel - 1) {
							//logger.log(`flushing ${sn}`);
							if(nextRange.start === undefined) {
								nextRange.start = frag.start;
							}
							nextRange.end = frag.end;
							flushingFrag = true;
							//this.flushRange.push({ start: frag.start, end: frag.end, type: type });
							frags[sn] = undefined;
						}
						else if (frag.level === currentLevel - 1) {
							if (frag.level === frag.maxLevel) {
								//logger.log(`flushing ${sn}`);
								if(nextRange.start === undefined) {
									nextRange.start = frag.start;
								}
								nextRange.end = frag.end;
								flushingFrag = true;
								//this.flushRange.push({ start: frag.start, end: frag.end, type: type });
								frags[sn] = undefined;
							}
						}
					}

					if (!flushingFrag && nextRange.start !== undefined) {
						this.flushRange.push({ start: nextRange.start, end: nextRange.end, type: type });
						nextRange.start = undefined;
						nextRange.end = undefined;
					}
				});

				if (nextRange.start !== undefined) {
					this.flushRange.push({ start: nextRange.start, end: nextRange.end, type: type });
					nextRange.start = undefined;
					nextRange.end = undefined;
				}
			}
		}

		if(this.flushRange.length > 0) {
			this.doFlush();
		}
		else {
			this.readyForData = true;
			this.hls.trigger(Event.BUFFER_FLUSHED);
		}
	}


	doDetach() {
		this.mediaSource.removeEventListener('sourceopen', this.onmso);
		this.mediaSource.removeEventListener('sourceended', this.onmse);
		this.mediaSource.removeEventListener('sourceclose', this.onmsc);

		this.doRemoveSourceBuffer();

		// Detach properly the MediaSource from the HTMLMediaElement as
		// suggested in https://github.com/w3c/media-source/issues/53.
		if (this.media) {
			URL.revokeObjectURL(this.media.src);
			this.media.removeAttribute('src'); //causes MediaSourceClose
			this.media.load();
		}
		this.mediaSource = null;

		this.media = null;
		this.pendingTracks = {};
		this.tracks = {};
		this.flushRange = [];
		this.segments = [];
		this.onmso = this.onmse = this.onmsc = null;
		this.hls.trigger(Event.MEDIA_DETACHED);

		this._needsDetach = false;
	}


	doRemoveSourceBuffer() {
		var sourceBuffer = this.sourceBuffer;
		for(var type in sourceBuffer) {
			if( type === 'audio' || type === 'video') {
				var sb = sourceBuffer[type];
				try {
					sb.removeEventListener('updateend', this.onsbue);
					sb.removeEventListener('error', this.onsbe);
					sb.removeEventListener('abort', this.onsba);
					if (this.mediaSource.readyState === 'open' || this.mediaSource.readyState === 'ended') {
						this.mediaSource.removeSourceBuffer(sb);
					}
					else {
						logger.warn(`doRemoveSourceBuffer: MediaSource is in an invalid state, probably closed. Would error or crash if we attempted to remove source buffer.`);
					}
				}
				catch(err) {
					logger.error(`doRemoveSourceBuffer: ${err.message} while removing SourceBuffer`);
				}
			}
		}
		sourceBuffer = {};
		this.sourceBuffer = {};
		this.appending = false;
	}


	set readyForData(value) {
		if (!this.flushRange || (this.flushRange && !(this.flushRange.length > 0) )) {
			this._readyForData = value;
		}
		else {
			this._readyForData = false;
		}
	}

	get readyForData() {
		return this._readyForData;
	}


	_nextRange(sb, startOffset, endOffset) {
		let i = 0;
		let success = false;
		let start = undefined;
		let end = undefined;

		for (i = 0; i < sb.buffered.length; i++) {
			start = sb.buffered.start(i);
			end = sb.buffered.end(i);

			start = startOffset;
			end = endOffset;

			if (Math.min(end, endOffset) - Math.max(start, startOffset) > 0.01 ) {
				success = true;
				break;
			}
		}

		let result = { success, start, end };

		return result;
	}



}

export default BufferController;



// WEBPACK FOOTER //
// ./src/hlsjs/controller/buffer-controller.js